import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { SuppliedItemResponseArgs } from './supplied_item/supplied_item2';
import SuppliedItem, { convertSuppliedItemResponseToArgs } from './supplied_item/supplied_item2';
import type { IndividualDomesticAirSuppliedItem } from './individual_domestic_air_supplied_item';

export type OrderItemOriginalPriceResponseArgs = {
  id: number;
  order_item_original_price_type?: number;
  price: number;
  tax_type_id: number;
  supplied_item_id: number;
  supplied_item?: SuppliedItemResponseArgs;
  original_price_trace_id: number;
  payment_method_id?: number;
  supplier_card_id: number;
  purchased_at: string;
  account_recorded_at: string;
};

export type OrderItemOriginalPriceArgs = {
  id?: number;
  orderItemOriginalPriceType?: number;
  price?: number;
  taxTypeId?: number;
  suppliedItemId?: number;
  suppliedItem?: SuppliedItem;
  originalPriceTraceId?: number;
  paymentMethodId?: number;
  supplierCardId?: number;
  purchasedAt?: Moment;
  accountRecordedAt?: Moment;
};

export const convertOrderItemOriginalPriceResponseToArgs = (response: OrderItemOriginalPriceResponseArgs) => {
  const args: OrderItemOriginalPriceArgs = {
    id: response.id,
    orderItemOriginalPriceType: response.order_item_original_price_type,
    price: response.price,
    taxTypeId: response.tax_type_id,
    suppliedItemId: response.supplied_item_id,
    suppliedItem: response.supplied_item
      ? new SuppliedItem(convertSuppliedItemResponseToArgs(response.supplied_item))
      : undefined,
    originalPriceTraceId: response.original_price_trace_id,
    paymentMethodId: response.payment_method_id,
    supplierCardId: response.supplier_card_id,
    purchasedAt: response.purchased_at ? moment(response.purchased_at) : undefined,
    accountRecordedAt: response.account_recorded_at ? moment(response.account_recorded_at) : undefined
  };
  return args;
};

export default class OrderItemOriginalPrice {
  @observable id?: number;
  @observable orderItemOriginalPriceType?: number;
  @observable price: number;
  @observable taxTypeId?: number;
  @observable suppliedItemId?: number;
  @observable suppliedItem?: SuppliedItem;
  @observable originalPriceTraceId?: number;
  @observable paymentMethodId?: number;
  @observable supplierCardId?: number;
  @observable purchasedAt?: Moment;
  @observable accountRecordedAt?: Moment;

  constructor(args: OrderItemOriginalPriceArgs) {
    this.id = args.id;
    this.orderItemOriginalPriceType = args.orderItemOriginalPriceType;
    this.price = args.price || 0;
    this.taxTypeId = args.taxTypeId;
    this.suppliedItemId = args.suppliedItemId;
    this.suppliedItem = args.suppliedItem;
    this.originalPriceTraceId = args.originalPriceTraceId;
    this.paymentMethodId = args.paymentMethodId;
    this.supplierCardId = args.supplierCardId;
    this.purchasedAt = args.purchasedAt;
    this.accountRecordedAt = args.accountRecordedAt;
  }

  get validationErrors(): string[] {
    const errors: string[] = [];
    if (this.price && this.price > 0 && !this.suppliedItemId) {
      errors.push('新卸値が0円以外の場合は仕入商品を指定してください。');
    }
    return errors;
  }

  get updateParams() {
    return {
      order_item_original_price_type: this.orderItemOriginalPriceType,
      price: this.price,
      tax_type_id: this.taxTypeId,
      supplied_item_id: this.suppliedItemId,
      original_price_trace_id: this.originalPriceTraceId,
      payment_method_id: this.paymentMethodId,
      supplier_card_id: this.supplierCardId,
      purchased_at: this.purchasedAt?.format('YYYY-MM-DD')
      // 以下3つのパラメーターはpayment_methodから導出されるものなので外からupdateできないため送らない
      // account_recorded_at: this.accountRecordedAt?.format('YYYY-MM-DD')
      // closing_day: this.closingDay?.format('YYYY-MM-DD'),
      // due_date: this.dueDate?.format('YYYY-MM-DD')
    };
  }

  individualSuppliedItemId(
    name: string,
    ticketingType: number,
    suppliedItems: SuppliedItem[],
    individualDomesticAirSuppliedItems: IndividualDomesticAirSuppliedItem[]
  ): void {
    const items = individualDomesticAirSuppliedItems.filter(item => item.ticketing_type === ticketingType);
    if (items.length > 0) {
      this.suppliedItemId = items[0].supplied_item_id;
    } else {
      this.suppliedItemId = 0; // Set to 0 or appropriate default value
    }
    // Set payment method based on supplied item
    const suppliedItem = suppliedItems?.find(s => s.id === this.suppliedItemId);
    if (suppliedItem?.paymentMethodId) {
      this.paymentMethodId = suppliedItem.paymentMethodId;
    }
  }

  defaultPaymentMethod(suppliedItems?: SuppliedItem[]) {
    if (suppliedItems) {
      const suppliedItem = suppliedItems?.find(s => s.id === this.suppliedItemId);

      return suppliedItem?.paymentMethodId;
    }

    return this.suppliedItem?.paymentMethodId;
  }
}
